// What each row of Match columns says (release 3.3, import redesign I2), as
// plain functions the node tests call. The screen (ColumnsStep.tsx) only
// draws what these return.
//
// # THE THREE TIERS
//
// The importer sends every column a tier with its suggestion (I1,
// packages/core/importer/match/tiers.go): MATCHED, REVIEW or UNMATCHED, and
// the source of the suggestion. A suggestion under the Review threshold never
// arrives as one: the API has already made that column extra detail. The
// screen shows each tier as a word with an icon, never colour alone, and:
//   - Matched rows fold under one line, because there is nothing to do there;
//   - Review rows stay open with the reason and a Confirm;
//   - Unmatched rows stay open: a person chooses a field, or leaves it as an
//     extra field, which keeps it.
// A person's own choice is final: it reads "Matched (you)" and a later
// suggestion never overwrites it (the draft keeps it as byPerson). A row a
// saved mapping matched reads "Matched (saved)".

import type { ImportAnalysis, ImportColumn, ImportColumnChoices, ImportField } from '../../lib/api'
import { KEEP_EXTRA, KEEP_EXTRA_LABEL, LEAVE_OUT, LEAVE_OUT_LABEL } from '../../lib/importWords'

export type ColumnTier = 'MATCHED' | 'REVIEW' | 'UNMATCHED'

export type ColumnRow = {
  col: ImportColumn
  // The column's header, or "Column 3" for one with none.
  name: string
  // What the column goes to now: a field key, KEEP_EXTRA or LEAVE_OUT.
  choice: string
  field?: ImportField
  tier: ColumnTier
  // The person chose this (not the importer), so it reads "(you)".
  byPerson: boolean
  // A saved mapping matched it, and nobody changed it since: "(saved)".
  saved: boolean
  // Up to three distinct values, whole; the screen cuts each to 28 characters.
  samples: string[]
  // The reason line under the choice.
  why: string
  // The chosen field is also chosen for another column and takes only one.
  clash: boolean
}

export const SAMPLE_CHARS = 28

// Up to three distinct, non-empty values; the screen cuts each to 28
// characters with an ellipsis, and the full value is the cell's title.
export function firstValues(samples: string[] | undefined): string[] {
  const out: string[] = []
  for (const s of samples ?? []) {
    const v = s.trim()
    if (!v || out.includes(v)) continue
    out.push(v)
    if (out.length === 3) break
  }
  return out
}

export function cutValue(v: string): string {
  const chars = Array.from(v)
  return chars.length <= SAMPLE_CHARS ? v : chars.slice(0, SAMPLE_CHARS - 1).join('') + '...'
}

// The importer's tier for its own suggestion. An API from before I1 sends no
// tier: the old status says the same thing.
export function serverTier(c: ImportColumn): ColumnTier {
  const t = c.tier
  if (t === 'MATCHED' || t === 'REVIEW' || t === 'UNMATCHED') return t
  if (c.status === 'MATCHED') return 'MATCHED'
  if (c.status === 'CHECK') return 'REVIEW'
  return 'UNMATCHED'
}

// What the importer itself would choose for the column.
export function suggestionOf(c: ImportColumn): string {
  if (c.field) return c.field
  return c.keep === 'LEAVE' ? LEAVE_OUT : KEEP_EXTRA
}

export function columnRows(
  analysis: ImportAnalysis,
  choices: ImportColumnChoices,
  confirmed: Record<string, boolean>,
  byPerson: ReadonlySet<string>,
): ColumnRow[] {
  const byKey = new Map(analysis.fields.map((f) => [f.key, f]))
  const usedBy = new Map<string, number>()
  for (const c of analysis.columns) {
    const v = choices[c.column] ?? suggestionOf(c)
    if (byKey.has(v)) usedBy.set(v, (usedBy.get(v) ?? 0) + 1)
  }
  return analysis.columns.map((c) => {
    const choice = choices[c.column] ?? suggestionOf(c)
    const field = byKey.get(choice)
    const suggested = choice === suggestionOf(c)
    const mine = byPerson.has(c.column) || !suggested
    let tier: ColumnTier
    if (mine) tier = field || choice === LEAVE_OUT || choice === KEEP_EXTRA ? 'MATCHED' : 'UNMATCHED'
    else if (confirmed[c.column]) tier = 'MATCHED'
    else tier = serverTier(c)
    // A column kept as an extra field by default, with no suggestion behind
    // it, is unmatched until somebody says so: the importer could not place it.
    if (!mine && !field && !confirmed[c.column] && serverTier(c) !== 'MATCHED') tier = 'UNMATCHED'
    let why = ''
    if (choice === LEAVE_OUT) why = 'Not imported.'
    else if (!field) why = c.why && !mine ? c.why : 'Kept as an extra field.'
    else if (mine) why = 'Your choice.'
    else why = c.why ?? ''
    return {
      col: c,
      name: c.column || 'Column ' + (c.index + 1),
      choice,
      field,
      tier,
      byPerson: mine,
      saved: !mine && tier === 'MATCHED' && c.source === 'saved',
      samples: firstValues(c.samples),
      why,
      clash: Boolean(field && !field.many && (usedBy.get(choice) ?? 0) > 1),
    }
  })
}

// The chip's word: the state is in the words, so colour is never the only
// signal.
export function chipWord(r: ColumnRow): string {
  if (r.tier === 'REVIEW') return 'Review'
  if (r.tier === 'UNMATCHED') return 'Unmatched'
  if (r.byPerson) return 'Matched (you)'
  if (r.saved) return 'Matched (saved)'
  return 'Matched'
}

export function tierCounts(rows: ColumnRow[]): Record<ColumnTier, number> {
  const out: Record<ColumnTier, number> = { MATCHED: 0, REVIEW: 0, UNMATCHED: 0 }
  for (const r of rows) out[r.tier]++
  return out
}

// The Review rows "Accept N suggestions" confirms: the importer's own
// suggestion, still standing. Unmatched and Matched rows are never touched.
export function acceptable(rows: ColumnRow[]): ColumnRow[] {
  return rows.filter((r) => r.tier === 'REVIEW' && !r.byPerson && r.field)
}

// The summary line's count of unmatched columns, and whether every one of
// them is kept as an extra field (nothing is lost unless "Do not import").
export function unmatchedWords(rows: ColumnRow[]): string {
  const un = rows.filter((r) => r.tier === 'UNMATCHED')
  if (un.length === 0) return '0 unmatched'
  const kept = un.every((r) => r.choice === KEEP_EXTRA)
  return un.length + ' unmatched' + (kept ? ', kept as extra ' + (un.length === 1 ? 'field' : 'fields') : '')
}

// Why Next is off, in one line, or "" when it is on.
export function whyNotNext(
  analysis: ImportAnalysis,
  rows: ColumnRow[],
  unanswered: number,
): string {
  const missing = missingRequired(analysis, rows)
  const clash = rows.find((r) => r.clash)
  if (missing.length > 0) return 'Choose a column for ' + listWords(missing.map((f) => f.label)) + ' first.'
  if (clash) return (clash.field?.label ?? '') + ' is chosen for more than one column. Keep it on one.'
  if (unanswered > 0) return 'Answer ' + (unanswered === 1 ? 'the question' : 'the ' + unanswered + ' questions') + ' above first.'
  return ''
}

// The required fields not yet given a column.
export function missingRequired(analysis: ImportAnalysis, rows: ColumnRow[]): ImportField[] {
  const used = new Set(rows.filter((r) => r.field).map((r) => r.choice))
  return analysis.fields.filter((f) => f.required && !used.has(f.key))
}

export function listWords(words: string[]): string {
  if (words.length <= 1) return words.join('')
  return words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1]
}

// The name a remembered mapping gets unless the person types one: the saved
// mapping the file matched, the recognised layout, or the file's name.
export function defaultMappingName(analysis: ImportAnalysis): string {
  const saved = analysis.savedMapping?.name
  if (saved) return saved
  if (analysis.layout?.applied && analysis.layout.label) return mappingName(analysis.layout.label)
  return mappingName(analysis.fileName.replace(/\.[^.]+$/, '')) || 'My vendor file'
}

// The longest name the server keeps for a mapping.
export const MAPPING_NAME_MAX = 120

export function mappingName(s: string): string {
  return Array.from(s.replace(/\s+/g, ' ').trim()).slice(0, MAPPING_NAME_MAX).join('').trim()
}

// What a file read again keeps: only the columns a person chose by hand. The
// rest were the old reading's suggestions, and the new reading's own
// suggestions take their place.
export function personsChoices(choices: ImportColumnChoices, byPerson: readonly string[]): ImportColumnChoices {
  const out: ImportColumnChoices = {}
  for (const c of byPerson) if (choices[c] !== undefined) out[c] = choices[c]
  return out
}

// ------------------------------------------------- the "Goes to" list

export type PickerOption = { value: string; label: string; group: string }

// The list's groups, in order (plan 4.3). The importer groups its fields by
// what they describe ("Who they are", "People"); the list folds those into
// six a person scans: the required fields first, then Identity, Contacts,
// Risk, Contract, and the rest. A group the importer adds later lands in
// Other, so no field is ever missing from the list.
export const PICKER_GROUPS = ['Required', 'Identity', 'Contacts', 'Risk', 'Contract', 'Other'] as const

const GROUP_OF: Record<string, (typeof PICKER_GROUPS)[number]> = {
  'Who they are': 'Identity',
  'Identity numbers': 'Identity',
  'Where they are': 'Identity',
  People: 'Contacts',
  'Data and access': 'Risk',
  'From your previous tool': 'Risk',
  'Contract and reviews': 'Contract',
  'Certificates and agreements': 'Contract',
}

// The two choices that are not a field, at the end of every list (and still
// there when a search matches no field).
export const NOT_OURS = 'Not one of our fields'
export const END_OPTIONS: PickerOption[] = [
  { value: KEEP_EXTRA, label: KEEP_EXTRA_LABEL, group: NOT_OURS },
  { value: LEAVE_OUT, label: LEAVE_OUT_LABEL, group: NOT_OURS },
]

export function pickerOptions(fields: ImportField[]): PickerOption[] {
  const out: PickerOption[] = []
  for (const g of PICKER_GROUPS) {
    for (const f of fields) {
      const at = f.required ? 'Required' : (GROUP_OF[f.group] ?? 'Other')
      if (at === g) out.push({ value: f.key, label: f.label + (f.required ? ' *' : ''), group: g })
    }
  }
  return [...out, ...END_OPTIONS]
}

// The options a search leaves: the fields whose label or group holds the
// words, then always the two that are not a field.
export function searchOptions(options: PickerOption[], q: string): PickerOption[] {
  const s = q.trim().toLowerCase()
  const fields = options.filter((o) => o.group !== NOT_OURS)
  const end = options.filter((o) => o.group === NOT_OURS)
  if (!s) return [...fields, ...end]
  return [...fields.filter((o) => o.label.toLowerCase().includes(s) || o.group.toLowerCase().includes(s)), ...end]
}
